import { createApi } from "@reduxjs/toolkit/query/react";
import { components } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

// API response type that includes status, message, and data
export type ApiMetricsResponse =
  components["schemas"]["ApiResponse_MergedCampaignMetricsBreakdown_"];

// The actual metrics data type
export type MergedCampaignMetricsBreakdown =
  components["schemas"]["MergedCampaignMetricsBreakdown"];

// API response type for MergedCampaignInfo
export type ApiMergedCampaignInfoResponse =
  components["schemas"]["ApiResponse_MergedCampaignInfo-Array_"];

// The actual MergedCampaignInfo data type
export type MergedCampaignInfo =
  | components["schemas"]["MergedCampaignInfo"][]
  | null;

// API response type for MergedCampaignMetricsLineItems
export type ApiMergedCampaignMetricsLineItemsResponse =
  components["schemas"]["ApiResponse_MergedCampaignMetricsLineItems_"];

// The actual MergedCampaignMetricsLineItems data type
export type MergedCampaignMetricsLineItems =
  | components["schemas"]["MergedCampaignMetricsLineItems"]
  | null;

// API response type for campaign-level IO metrics
type ApiCampaignInsertionOrderMetricsBreakdownResponse =
  components["schemas"]["ApiResponse_CampaignInsertionOrderMetricsBreakdown_"];

// Campaign-level IO metrics data type
export type CampaignInsertionOrderMetricsBreakdown =
  components["schemas"]["CampaignInsertionOrderMetricsBreakdown"];

export const internalCampaignsMergeApi = createApi({
  reducerPath: "internalCampaignsMergeApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Campaigns",
    "UserCampaigns",
    "MergedCampaigns",
    "MergedCampaignMetrics",
  ],
  endpoints: (builder) => ({
    // Get campaign metrics with merged groups
    getMetrics: builder.query<
      MergedCampaignMetricsBreakdown,
      { campaignId: string }
    >({
      query: ({ campaignId }) =>
        `/internalCampaignMerge/campaign/${campaignId}/metrics`,
      providesTags: (result, error, { campaignId }) => [
        { type: "MergedCampaigns", id: "LIST" },
        { type: "MergedCampaignMetrics", id: "LIST" },
        { type: "Campaigns", id: campaignId },
      ],
      transformResponse: (
        response: ApiMetricsResponse
      ): MergedCampaignMetricsBreakdown => {
        // The API returns { status, data, message } but we only need the data
        // Extract the data property which contains the MergedCampaignMetricsBreakdown
        if (response.data) {
          return response.data;
        }
        // Fallback to empty structure if no data
        return {
          summary: {
            plays: 0,
            totalImpressions: 0,
            totalClicks: 0,
            totalCost: 0,
            avgCTR: 0,
            avgCPC: 0,
            avgCPM: 0,
            totalConversions: 0,
            totalCompleteViews: 0,
            avgVTR: 0,
            avgCPV: 0,
            platforms: {},
            topCampaigns: {
              byClicks: [],
              byImpressions: [],
              bySpend: [],
            },
            uniqueUsers: 0,
          },
          dailyData: [],
          mergedGroups: [],
          platform: "NATADS",
        };
      },
    }),
    // Get Merged Campaign Metrics
    getMergedCampaignMetrics: builder.query<
      components["schemas"]["MergedCampaignMetrics"] | null,
      {
        mergedCampaignId: string;
        startDate?: string; // Optional start date in YYYY-MM-DD format
        endDate?: string; // Optional end date in YYYY-MM-DD format
      }
    >({
      query: ({ mergedCampaignId, startDate, endDate }) => {
        // Build query parameters if dates are provided
        const queryParams = new URLSearchParams();
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        return `/internalCampaignMerge/${mergedCampaignId}/metrics${
          queryString ? `?${queryString}` : ""
        }`;
      },
      providesTags: (result, error, { mergedCampaignId }) => [
        { type: "MergedCampaignMetrics", id: mergedCampaignId },
        { type: "MergedCampaigns", id: mergedCampaignId },
        { type: "Campaigns", id: "LIST" },
      ],
      transformResponse: (
        response: components["schemas"]["ApiResponse_MergedCampaignMetrics_"]
      ): components["schemas"]["MergedCampaignMetrics"] | null => {
        return response.data;
      },
    }),
    // Get Merged Campaign Info
    getMergedCampaignInfo: builder.query<
      MergedCampaignInfo | null,
      { campaignId: string }
    >({
      query: ({ campaignId }) =>
        `/internalCampaignMerge/campaign/${campaignId}/info`,
      providesTags: (result, error, { campaignId }) => [
        { type: "MergedCampaigns", id: campaignId },
        { type: "Campaigns", id: "LIST" },
      ],
      transformResponse: (
        response: ApiMergedCampaignInfoResponse
      ): MergedCampaignInfo | null => {
        return response.data;
      },
    }),
    // Get Merged Line Item Metrics
    getMergedLineItemMetrics: builder.query<
      MergedCampaignMetricsLineItems,
      {
        mergedCampaignId: string;
        startDate?: string; // Optional start date in YYYY-MM-DD format
        endDate?: string; // Optional end date in YYYY-MM-DD format
      }
    >({
      query: ({ mergedCampaignId, startDate, endDate }) => {
        // Build query parameters if dates are provided
        const queryParams = new URLSearchParams();
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        return `/internalCampaignMerge/${mergedCampaignId}/line-items-metrics${
          queryString ? `?${queryString}` : ""
        }`;
      },
      providesTags: (result, error, { mergedCampaignId }) => [
        { type: "MergedCampaignMetrics", id: mergedCampaignId },
        { type: "MergedCampaigns", id: mergedCampaignId },
      ],
      transformResponse: (
        response: ApiMergedCampaignMetricsLineItemsResponse
      ): MergedCampaignMetricsLineItems => {
        return response.data;
      },
    }),
    getMergedInsertionOrderMetrics: builder.query<
      components["schemas"]["MergedCampaignMetricsInsertionOrders"],
      {
        mergedCampaignId: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ mergedCampaignId, startDate, endDate }) => {
        const queryParams = new URLSearchParams();
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        return {
          url: `/internalCampaignMerge/merged/${mergedCampaignId}/insertion-orders${
            queryString ? `?${queryString}` : ""
          }`,
        };
      },
      transformResponse: (
        response: components["schemas"]["ApiResponse_MergedCampaignMetricsInsertionOrders_"]
      ): components["schemas"]["MergedCampaignMetricsInsertionOrders"] => {
        if (!response.data) {
          throw new Error("No insertion order metrics data available");
        }
        return response.data;
      },
    }),
    getCampaignInsertionOrderMetricsBreakdown: builder.query<
      CampaignInsertionOrderMetricsBreakdown | null,
      {
        campaignId: string;
        startDate?: string;
        endDate?: string;
      }
    >({
      query: ({ campaignId, startDate, endDate }) => {
        const queryParams = new URLSearchParams();
        if (startDate) queryParams.append("startDate", startDate);
        if (endDate) queryParams.append("endDate", endDate);

        const queryString = queryParams.toString();
        return `/internalCampaignMerge/campaign/${campaignId}/insertion-orders/metrics${
          queryString ? `?${queryString}` : ""
        }`;
      },
      providesTags: (result, error, { campaignId }) => [
        { type: "MergedCampaignMetrics", id: campaignId },
        { type: "Campaigns", id: campaignId },
      ],
      transformResponse: (
        response: ApiCampaignInsertionOrderMetricsBreakdownResponse
      ): CampaignInsertionOrderMetricsBreakdown | null => {
        return response.data ?? null;
      },
    }),
  }),
});

export const {
  useGetMetricsQuery,
  useGetMergedCampaignMetricsQuery,
  useGetMergedCampaignInfoQuery,
  useGetMergedLineItemMetricsQuery,
  useGetMergedInsertionOrderMetricsQuery,
  useGetCampaignInsertionOrderMetricsBreakdownQuery,
} = internalCampaignsMergeApi;
